import {
  useWSModalCustom,
  useWSSnackbar,
  WSAlert,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { WorkLog } from "@wingspanhq/wingspan-api-sdk";
import { ErrorAlert } from "@components/ErrorAlert";
import { queriesWorkLogs } from "@query";

type Props = {
  workLog: WorkLog;
} & WSModalCustomProps<{
  success?: boolean;
}>;

export const ModalSubmitForReview: React.FC<Props> = ({
  workLog,
  ...modalProps
}) => {
  const { openSnackbar } = useWSSnackbar();
  const [submitForReview, submitMeta] =
    queriesWorkLogs.submitWorkLog.useMutation();

  return (
    <WSModal
      title="Submit for review"
      actions={{
        alignment: "fill",
        buttons: [
          {
            kind: "Secondary",
            label: "No, keep editing",
            onClick: () => {
              modalProps.onClose({ success: false });
            }
          },
          {
            kind: "Primary",
            label: "Yes, submit for review",
            onClick: () => {
              // TODO(v3): submitWorkLog takes no comment — v1's payeeOwnedData.comment:null clear on submit is dropped.
              submitForReview(
                { workLogId: workLog.id! },
                {
                  onSuccess: () => {
                    modalProps.onClose({ success: true });
                    openSnackbar({
                      icon: { name: "check" },
                      message: "Work log submitted for review"
                    });
                  }
                  // A failure is rendered in the modal from `submitMeta.error`
                  // rather than snackbarred, so it sits next to the button that
                  // failed and stays until the contractor acts on it.
                }
              );
            },
            loading: submitMeta.isLoading
          }
        ]
      }}
      {...modalProps}
    >
      <WSList gap="XL">
        <WSText.ParagraphSm color="gray500">
          Are you sure you want to submit the work log for review?
        </WSText.ParagraphSm>

        {/* No `forceShowRawError`: a submit rejection carries no `detail`, so
            the raw string would be the problem `title` — the literal
            "Validation Error" — shown in place of the context default. Once
            PLA-3701 emits a `detailCode`, name the rule with a `byCode` entry
            instead. */}
        <ErrorAlert error={submitMeta.error} context="WorkLog" />

        <WSAlert
          theme="info"
          title="About work log submissions"
          icon="info-circle"
        >
          Once a work log is submitted, it cannot be edited while in review.
          Please ensure the work summary is accurate before submitting for
          client review. To continue editing the work log, click &quot;No, keep
          editing&quot;.
        </WSAlert>
      </WSList>
    </WSModal>
  );
};

export const useModalSubmitForReview = () =>
  useWSModalCustom(ModalSubmitForReview);
